import { v4 as uuid } from "uuid";
import { batchGetFieldInstancesByFormGroup } from "utils/batchGetFieldInstancesByFormGroup";
import batchGetFieldTemplatesByFormTemplate from "utils/batchGetFieldTemplatesByFormTemplate";
import { batchGetFormGroups } from "utils/batchGetFormGroups";
import batchGetOptionsByFormTemplate from "utils/batchGetOptionsByFormTemplate";
import handleBatchGetFormTemplates from "../utils/handleBatchGetFormTemplates";
import { bulkRetrieveFormTemplates } from "./queries/formTemplateQueries";
import {
	CURRENT_JSON_VERSION,
	FieldContinuationDataForResolver,
	FieldInstanceForResolver,
	FieldTemplateForResolver,
	FormGroupForResolver,
	FormInstanceForResolver,
	FormTemplateForResolver,
	Image,
	ImageBinary,
	JsonStructure,
	Option,
	Project,
	ProjectForResolver,
	Revision,
	Source,
	SyncConfig,
	UserForResolver,
} from "./definitions";
import {
	bulkSaveFieldContinuationDatas,
	createFieldContinuationDataForResolver,
	createMergedFieldContinuationData,
} from "./queries/fieldContinuationDataQueries";
import { bulkSaveFieldInstances, createFieldForBatchResolver, createMergedFieldForBatchResolver } from "./queries/fieldInstanceQueries";
import { bulkSaveFieldTemplates, createFieldTemplateForBatchResolver, createMergedFieldTemplateForBatchResolver } from "./queries/fieldTemplateQueries";
import { bulkSaveFormGroupDocs, createFormGroupForBatchResolver, createMergedFormGroup } from "./queries/formGroupQueries";
import { bulkSaveFormInstances, createFormInstanceForBatchResolver, createMergedFormInstance } from "./queries/formInstanceQueries";
import { bulkSaveFormTemplates, createFormTemplateForBatchResolver, createMergedFormTemplate, downloadFormTemplateJSON } from "./queries/formTemplateQueries";
import {
	bulkDeleteImageBinaryDocs,
	bulkGetImageBinaries,
	deleteImagesFromS3,
	getAllImagesFromOfflineAndSaveToS3,
	getAllImagesFromOnlineAndSaveToBinary,
} from "./queries/imageBinaryQueries";
import { bulkSaveImageDocs, createImageForBatchResolver, createdMergedImageDocForResolver } from "./queries/imageQueries";
import { bulkSaveOptions, createMergedOptionForBatchResolver, createOptionForBatchResolver } from "./queries/optionQueries";
import { bulkSaveProjects, createMergedProjectForPut, createProjectForPut } from "./queries/projectQueries";
import { createRevisionForBatchResolver, getRevisionsByFormGroup, putRevisions } from "./queries/revisionQueries";
import { batchGetFormInstancesAndFormTemplateByFormGroup } from "utils/batchGetFormInstancesByFormGroup";
import { batchGetImagesByFormGroup } from "utils/batchGetImagesByFormGroup";
import { generateClient } from "aws-amplify/api";
import { uploadData } from "aws-amplify/storage";
import { handleBatchCrud } from "utils/handleBatchCrud";
import { serializeMergeData } from "utils/serializeMergeData";
import batchCreateProjects from "utils/batchCreateProjects";
import batchCreateFormGroups from "utils/batchCreateFormGroups";
import batchCreateFormInstances from "utils/batchCreateFormInstances";
import batchCreateFieldInstances from "utils/batchCreateFieldInstances";
import batchCreateFormTemplates from "utils/batchCreateFormTemplates";
import batchCreateFieldTemplates from "utils/batchCreateFieldTemplates";
import batchCreateOptions from "utils/batchCreateOptions";
import batchCreateFieldContinuationData from "utils/batchCreateFieldContinuationData";
import { recordRepairBreadcrumb, recordSentry } from "utils/recordSentry";
import { consolidateDuplicateTableInstances, TableConsolidation } from "utils/consolidateDuplicateTableInstances";
import { GiLog } from "react-icons/gi";

const client = generateClient({ authMode: "userPool" });

interface MergeData {
	/** A map of id to fieldContinuationData */
	continuationDatas: Map<string, FieldContinuationDataForResolver>;
	/** A map of id to formGroup */
	formGroups: Map<string, FormGroupForResolver>;
	/** A map of id to formInstance */
	formInstances: Map<string, FormInstanceForResolver>;
	/** A map of id to formTemplate */
	formTemplates: Map<string, FormTemplateForResolver>;
	/** A map of id to fieldInstance */
	fieldInstances: Map<string, FieldInstanceForResolver>;
	/** A map of id to fieldTemplate */
	fieldTemplates: Map<string, FieldTemplateForResolver>;
	/** A map of id to image */
	images: Map<string, Image>;
	/** If the source database is remote, and if there are images with keys, they could be downloaded */
	imagesCouldBeDownloaded?: boolean;
	/** A map of id to option */
	options: Map<string, Option>;
	/** A map of id to project */
	projects: Map<string, ProjectForResolver>;
	/** A map of id to user */
	users: Map<string, UserForResolver>;
	/** A map of id to revision */
	revisions: Map<string, Revision>;
}

interface TemplateMergeData {
	/** A map of id to fieldContinuationData */
	continuationDatas: Map<string, FieldContinuationDataForResolver>;
	/** A map of id to formTemplate */
	formTemplates: Map<string, FormTemplateForResolver>;
	/** A map of id to fieldTemplate */
	fieldTemplates: Map<string, FieldTemplateForResolver>;
	/** A map of id to option */
	options: Map<string, Option>;
}

interface TemplateMergeResult {
	finalContinuationDatas: FieldContinuationDataForResolver[];
	finalFieldTemplates: FieldTemplateForResolver[];
	finalFormTemplates: FormTemplateForResolver[];
	finalOptions: Option[];
	finalSelectedFormTemplates: Map<string, FormTemplateForResolver>;
	allProcessedFormTemplates: Map<string, FormTemplateForResolver>;
	allFieldTemplates: Map<string, FieldTemplateForResolver>;
}

/**
 * Given an array of type T and a map, loops through the array and sets each element's id to the element in the map
 * @author Eric Webb <ewebb@factorearth.com>
 * @param arr Array of somethings (something has id)
 * @param map A map of type string (id) to something
 */
export function iterateArrayAndInsertIntoMap<T extends { id: string }>(arr: T[], map: Map<string, T>, parser: (element: T) => T) {
	for (const element of arr) {
		const parsedElement = parser(element);
		map.set(parsedElement.id, parsedElement);
	}
}

/**
 * Given an array of type T and a map, loops through the array and sets each element's id to the element in the map
 * @author Eric Webb <ewebb@factorearth.com>
 * @param arr Array of somethings (something has id)
 * @param map A map of type string (id) to something
 */
export function iterateImagesAndInsertIntoMap(arr: Image[], map: Map<string, Image>, parser: (element: Image) => Image) {
	let imagesToDownload = false;
	for (const element of arr) {
		const parsedElement = parser(element);
		if (parsedElement.key) imagesToDownload = true;
		map.set(parsedElement.id, parsedElement);
	}
	return imagesToDownload;
}

/**
 * Give the formGroup ids, gets all data from the database and returns it in the json format
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The ids of the formGroups we want to download
 * @param online Whether or not the user is online
 * @returns The data from the database in the json structure
 */
export async function downloadFormGroupJson(
	project: ProjectForResolver | Project,
	ids: string[],
	syncConfig: SyncConfig,
	updateBanner?: (start: number, message: string) => void,
): Promise<JsonStructure> {
	updateBanner?.(13, "Retrieving Form Groups");
	const finalProject = createProjectForPut(project);
	const projects = [finalProject];
	const continuationDatas: FieldContinuationDataForResolver[] = [];
	const fieldInstances: FieldInstanceForResolver[] = [];
	const revisions: Revision[] = [];
	const images: Image[] = [];
	const fieldTemplates: FieldTemplateForResolver[] = [];
	const formGroups: FormGroupForResolver[] = [];
	const formInstances: FormInstanceForResolver[] = [];
	const formTemplates: FormTemplateForResolver[] = [];
	const options: Option[] = [];
	const jsonStructure: JsonStructure = {
		continuationDatas,
		fieldInstances,
		fieldTemplates,
		formGroups,
		formInstances,
		formTemplates,
		revisions,
		images,
		options,
		projects,
		version: CURRENT_JSON_VERSION,
	};
	// If the source is remote, or in the case where we're uploading json, check if we're online
	const fetchFromRemote = syncConfig.source === "remote" || (syncConfig.source === "json" && syncConfig.online);
	// Cause if you're not online, you can't grab remote silly
	if (!syncConfig.online && fetchFromRemote) return jsonStructure;
	const retrievedFormGroups = await batchGetFormGroups(ids, fetchFromRemote);
	updateBanner?.(25, "Retrieving Instance Data");
	const formTemplateIdToFormTemplate: Map<string, FormTemplateForResolver> = new Map();
	const instancePromises = [];
	for (const formGroup of retrievedFormGroups) {
		// LEAVE THIS IF STATEMENT. If a user is online, syncing a list of offline formGroups, including formGroups that don't exist online,
		// The batchGet will return null for the id that isn't online, resulting in this process shitting itself
		if (formGroup) {
			instancePromises.push(
				getInstanceData(formGroup, fetchFromRemote, formInstances, formTemplateIdToFormTemplate, formTemplates, fieldInstances, images, revisions),
			);
			if (syncConfig.destination === "remote" || syncConfig.destination === "local") formGroup.syncedAt = new Date().toISOString();
			formGroups.push(formGroup);
		}
	}
	await Promise.all(instancePromises);
	updateBanner?.(38, "Retrieving Template Data");
	// YOU ARE HERE THIS NEEDS TO CHANGE AND ACTUALLY GRAB ALL THE TEMPLATES I BELIEVE!
	for (const formTemplate of formTemplates) {
		await getTemplateData(formTemplate.id, fetchFromRemote, fieldTemplates, continuationDatas, options);
	}
	return jsonStructure;
}

/**
 * Given the formTemplate id and whether or not the user is online, queries the relevant db and pushes the fieldTempaltes, continuationDatas, and options into their respective arrays upon
 * fetching
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the formTemplate we're grabbing template data from
 * @param online Whether or not the user is online
 * @param fieldTemplates An array we want to push fieldTemplates into
 * @param continuationDatas An array we want to push continuationDatas into
 * @param options An array we want to push options into
 */
export async function getTemplateData(
	formTemplateId: string,
	online: boolean,
	fieldTemplates: FieldTemplateForResolver[],
	continuationDatas: FieldContinuationDataForResolver[],
	options: Option[],
) {
	const [fieldTemplatesWithContData, retrievedOptions] = await Promise.all([
		batchGetFieldTemplatesByFormTemplate(formTemplateId, online),
		batchGetOptionsByFormTemplate(formTemplateId, online),
	]);
	for (const fieldTemplate of fieldTemplatesWithContData.fieldTemplates) {
		fieldTemplates.push(fieldTemplate);
	}
	for (const contData of fieldTemplatesWithContData.fieldContDatas) {
		continuationDatas.push(contData);
	}
	for (const option of retrievedOptions) {
		options.push(option);
	}
}

/**
 * Enhances the receiverData with form templates from donorData and their associated data
 * Only adds templates if they are newer than the corresponding template in donorData or receiverData
 * @param receiverData The MergeData object to enhance with templates
 * @param donorData The MergeData object to get template IDs from and compare updatedAt timestamps against
 * @param fetchFromRemote Whether to fetch from remote or local database
 */
export async function addAllFormTemplatesToMergeData(receiverData: MergeData, donorData: MergeData, fetchFromRemote: boolean) {
	// Extract template IDs only from donorData - we only need to enhance receiverData with templates that will be part of the merge
	const donorTemplateIds = Array.from(donorData.formTemplates.keys());

	if (donorTemplateIds.length === 0) {
		return; // No templates to process
	}

	// Fetch only the templates that exist in donorData
	let fetchedTemplates: FormTemplateForResolver[] = [];

	if (fetchFromRemote) {
		fetchedTemplates = await handleBatchGetFormTemplates(donorTemplateIds);
	} else {
		fetchedTemplates = await bulkRetrieveFormTemplates(donorTemplateIds);
	}

	// Process each fetched template
	for (const fetchedTemplate of fetchedTemplates) {
		if (!fetchedTemplate) continue; // Skip null/undefined templates

		// Get the corresponding donorData template
		const donorTemplate = donorData.formTemplates.get(fetchedTemplate.id);
		if (!donorTemplate) continue; // Skip if no donor template

		// Get existing receiverData template
		const existingReceiverTemplate = receiverData.formTemplates.get(fetchedTemplate.id);

		// Get timestamps for comparison
		const fetchedTimestamp = new Date(fetchedTemplate.updatedAt).getTime();
		const donorTimestamp = new Date(donorTemplate.updatedAt).getTime();
		// const receiverTimestamp = existingReceiverTemplate ? new Date(existingReceiverTemplate.updatedAt).getTime() : 0;

		// Only process if donorData is newer than the fetched template, OR if receiverData doesn't have it and fetched is newer than donor
		const shouldProcessTemplate = donorTimestamp > fetchedTimestamp || (!existingReceiverTemplate && fetchedTimestamp > donorTimestamp);

		if (shouldProcessTemplate) {
			// Add the fetched template to receiverData for proper comparison in handleMergingTemplates
			receiverData.formTemplates.set(fetchedTemplate.id, fetchedTemplate);

			// Get the template data for this template and add to receiverData
			const fieldTemplatesArray: FieldTemplateForResolver[] = [];
			const continuationDatasArray: FieldContinuationDataForResolver[] = [];
			const optionsArray: Option[] = [];

			await getTemplateData(fetchedTemplate.id, fetchFromRemote, fieldTemplatesArray, continuationDatasArray, optionsArray);

			// Add the retrieved data to the receiverData maps
			// Since we're using a newer FormTemplate, we need to update ALL its children unconditionally
			for (const fieldTemplate of fieldTemplatesArray) {
				receiverData.fieldTemplates.set(fieldTemplate.id, fieldTemplate);
			}

			for (const continuationData of continuationDatasArray) {
				receiverData.continuationDatas.set(continuationData.id, continuationData);
			}

			for (const option of optionsArray) {
				receiverData.options.set(option.id, option);
			}
		}
	}

	return receiverData;
}

/**
 * Given a formGroup and whether or not the user is online, fetches all instance data from the appropriate database
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formGroup The formGroup we want to get instance data for
 * @param online Whether or not the user is online
 * @param formInstances The array we want to push formInstances into
 * @param formTemplateIdToFormTemplate A map tracking what formTemplates we have already
 * @param formTemplates The array we want to push formTemplates into
 * @param fieldInstances The array we want to push fieldInstances into
 */
export async function getInstanceData(
	formGroup: FormGroupForResolver,
	online: boolean,
	formInstances: FormInstanceForResolver[],
	formTemplateIdToFormTemplate: Map<string, FormTemplateForResolver>,
	formTemplates: FormTemplateForResolver[],
	fieldInstances: FieldInstanceForResolver[],
	images: Image[],
	revisions: Revision[],
) {
	// Gonna promisify the revisions because each field instance must retrieve their own for now.
	// const revisionPromises: Promise<Revision[]> = [];
	// let resolvedRevisions: Revision[][] = [];
	const [formInstancesResponse, fieldInstanceResponse, imageRespnse, revisionsResponse] = await Promise.all([
		batchGetFormInstancesAndFormTemplateByFormGroup(formGroup.id, online),
		batchGetFieldInstancesByFormGroup(formGroup.id, online),
		batchGetImagesByFormGroup(formGroup.id, online),
		getRevisionsByFormGroup(formGroup.id, online),
	]);
	// https://dev.to/uilicious/javascript-array-push-is-945x-faster-than-array-concat-1oki
	// Performing a push operation is substantially faster than just concatting. This should make sense, since concat is declaring a new array every time
	for (const formInstance of formInstancesResponse.formInstances) {
		formInstances.push(formInstance);
	}

	for (const formTemplate of formInstancesResponse.formTemplates) {
		if (formTemplate) {
			const existing = formTemplateIdToFormTemplate.get(formTemplate.id);
			if (!existing) {
				formTemplates.push(formTemplate);
				formTemplateIdToFormTemplate.set(formTemplate.id, formTemplate);
			}
		}
	}
	for (const fieldInstance of fieldInstanceResponse) {
		fieldInstances.push(fieldInstance);
	}
	for (const image of imageRespnse) {
		images.push(image);
	}

	for (const revision of revisionsResponse) {
		revisions.push(revision);
	}
}

/**
 * Given our json, iterates and maps through each array so we get all of our maps, returning them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonStructure The json from one of our files
 * @returns The map structure we use when merging things
 */
export function prepJsonForMerge(jsonStructure: JsonStructure): MergeData {
	// Run through each array of everything, turn them into a map, return them
	const continuationDataMap: Map<string, FieldContinuationDataForResolver> = new Map();
	const formGroupMap: Map<string, FormGroupForResolver> = new Map();
	const formInstanceMap: Map<string, FormInstanceForResolver> = new Map();
	const formTemplateMap: Map<string, FormTemplateForResolver> = new Map();
	const fieldInstanceMap: Map<string, FieldInstanceForResolver> = new Map();
	const fieldTemplateMap: Map<string, FieldTemplateForResolver> = new Map();
	const imageMap: Map<string, Image> = new Map();
	const optionMap: Map<string, Option> = new Map();
	const projectMap: Map<string, ProjectForResolver> = new Map();

	// STARTING WITH THIS, MAY HAVE TO CHANGE TO FIELD INSTANCEID: ARRAY OF REVISIONS
	const revisionMap: Map<string, Revision> = new Map();
	// There will be no user data in the json
	const userMap: Map<string, UserForResolver> = new Map();
	const mergeData: MergeData = {
		continuationDatas: continuationDataMap,
		formGroups: formGroupMap,
		formInstances: formInstanceMap,
		formTemplates: formTemplateMap,
		fieldInstances: fieldInstanceMap,
		fieldTemplates: fieldTemplateMap,
		images: imageMap,
		options: optionMap,
		projects: projectMap,
		revisions: revisionMap,
		users: userMap,
	};
	iterateArrayAndInsertIntoMap(jsonStructure.continuationDatas, continuationDataMap, createFieldContinuationDataForResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.formGroups, formGroupMap, createFormGroupForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.formInstances, formInstanceMap, createFormInstanceForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.formTemplates, formTemplateMap, createFormTemplateForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.fieldInstances, fieldInstanceMap, createFieldForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.fieldTemplates, fieldTemplateMap, createFieldTemplateForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.images, imageMap, createImageForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.options, optionMap, createOptionForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.projects, projectMap, createProjectForPut);
	iterateArrayAndInsertIntoMap(jsonStructure.revisions, revisionMap, createRevisionForBatchResolver);
	return mergeData;
}

/**
 * Given our json, iterates and maps through each array so we get all of our maps, returning them for templates
 * @author Robbie <rmartin@factorearth.com>
 * @param jsonStructure The json from one of our files
 * @returns The map structure we use when merging things
 */
export function prepTemplateJsonForMerge(jsonStructure: JsonStructure): TemplateMergeData {
	// Run through each array of everything, turn them into a map, return them
	const continuationDataMap: Map<string, FieldContinuationDataForResolver> = new Map();
	const formTemplateMap: Map<string, FormTemplateForResolver> = new Map();
	const fieldTemplateMap: Map<string, FieldTemplateForResolver> = new Map();
	const optionMap: Map<string, Option> = new Map();

	const mergeData: TemplateMergeData = {
		continuationDatas: continuationDataMap,
		formTemplates: formTemplateMap,
		fieldTemplates: fieldTemplateMap,
		options: optionMap,
	};
	iterateArrayAndInsertIntoMap(jsonStructure.continuationDatas, continuationDataMap, createFieldContinuationDataForResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.formTemplates, formTemplateMap, createFormTemplateForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.fieldTemplates, fieldTemplateMap, createFieldTemplateForBatchResolver);
	iterateArrayAndInsertIntoMap(jsonStructure.options, optionMap, createOptionForBatchResolver);
	return mergeData;
}

/**
 * Given the user doc and whether or not the user is online, grabs everything from the database that would need to be merged
 * @author Eric Webb <ewebb@factorearth.com>
 * @param online Whether or not the user is online
 * @param userDoc The user's document
 * @param project The project the user is in
 * @param formGroupIds The ids of the formGroups the user is looking to merge
 * @returns The merge data needed for the merge functin
 */
export async function prepDbForMerge(userDoc: UserForResolver, syncConfig: SyncConfig, formGroupIds: string[], project?: Project): Promise<MergeData> {
	const continuationDataMap: Map<string, FieldContinuationDataForResolver> = new Map();
	const formGroupMap: Map<string, FormGroupForResolver> = new Map();
	const formInstanceMap: Map<string, FormInstanceForResolver> = new Map();
	const formTemplateMap: Map<string, FormTemplateForResolver> = new Map();
	const fieldInstanceMap: Map<string, FieldInstanceForResolver> = new Map();
	const fieldTemplateMap: Map<string, FieldTemplateForResolver> = new Map();
	const imageMap: Map<string, Image> = new Map();
	const optionMap: Map<string, Option> = new Map();
	const projectMap: Map<string, ProjectForResolver> = new Map();
	const userMap: Map<string, UserForResolver> = new Map();
	const revisionMap: Map<string, Revision> = new Map();
	userMap.set(userDoc.id, userDoc);
	const mergeData: MergeData = {
		continuationDatas: continuationDataMap,
		formGroups: formGroupMap,
		formInstances: formInstanceMap,
		formTemplates: formTemplateMap,
		fieldInstances: fieldInstanceMap,
		fieldTemplates: fieldTemplateMap,
		images: imageMap,
		options: optionMap,
		projects: projectMap,
		revisions: revisionMap,
		users: userMap,
	};
	if (project) {
		const jsonStructure = await downloadFormGroupJson(project, formGroupIds, syncConfig);
		iterateArrayAndInsertIntoMap(jsonStructure.continuationDatas, continuationDataMap, createFieldContinuationDataForResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.fieldInstances, fieldInstanceMap, createFieldForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.fieldTemplates, fieldTemplateMap, createFieldTemplateForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.formGroups, formGroupMap, createFormGroupForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.formInstances, formInstanceMap, createFormInstanceForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.formTemplates, formTemplateMap, createFormTemplateForBatchResolver);
		const imagesCouldBeDownloaded =
			syncConfig.source === "remote"
				? iterateImagesAndInsertIntoMap(jsonStructure.images, imageMap, createImageForBatchResolver)
				: iterateArrayAndInsertIntoMap(jsonStructure.images, imageMap, createImageForBatchResolver);
		mergeData.imagesCouldBeDownloaded = !!imagesCouldBeDownloaded;
		iterateArrayAndInsertIntoMap(jsonStructure.options, optionMap, createOptionForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.projects, projectMap, createProjectForPut);
		iterateArrayAndInsertIntoMap(jsonStructure.revisions, revisionMap, createRevisionForBatchResolver);
	}
	return mergeData;
}

/**
 * Given the user doc and whether or not the user is online, grabs everything templatey from the database that would need to be merged
 * @author Robbie <rmartin@factorearth.com>
 * @param formTemplateId of the template needing retrieval
 * @param syncConfig the syncConfig
 * @returns The merge data needed for the merge functin
 */
export async function prepDbForTemplateMerge(formTemplateId: string, syncConfig: SyncConfig): Promise<TemplateMergeData> {
	const continuationDataMap: Map<string, FieldContinuationDataForResolver> = new Map();
	const formTemplateMap: Map<string, FormTemplateForResolver> = new Map();
	const fieldTemplateMap: Map<string, FieldTemplateForResolver> = new Map();
	const optionMap: Map<string, Option> = new Map();
	const mergeData: TemplateMergeData = {
		continuationDatas: continuationDataMap,
		formTemplates: formTemplateMap,
		fieldTemplates: fieldTemplateMap,
		options: optionMap,
	};

	const jsonStructure = await downloadFormTemplateJSON(formTemplateId, syncConfig.online);
	if (jsonStructure) {
		iterateArrayAndInsertIntoMap(jsonStructure.continuationDatas, continuationDataMap, createFieldContinuationDataForResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.fieldTemplates, fieldTemplateMap, createFieldTemplateForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.formTemplates, formTemplateMap, createFormTemplateForBatchResolver);
		iterateArrayAndInsertIntoMap(jsonStructure.options, optionMap, createOptionForBatchResolver);
	}

	return mergeData;
}

interface MergeDataToSave {
	/** The project documents we want to save to the database */
	projects: ProjectForResolver[];
	/** The formGroup documents we want to save to the database */
	formGroups: FormGroupForResolver[];
	/** The formInstance documents we want to save to the database */
	formInstances: FormInstanceForResolver[];
	/** The fieldinstance documents we want to save to the database */
	fieldInstances: FieldInstanceForResolver[];
	/** The formTemplate documents we want to save to the database */
	formTemplates: FormTemplateForResolver[];
	/** The fieldTemplate documents we want to save to the database */
	fieldTemplates: FieldTemplateForResolver[];
	/** The option documents we want to save to the database */
	options: Option[];
	/** The fieldContinuationData documents we want to save to the database */
	continuationDatas: FieldContinuationDataForResolver[];
	/** The image documents we want to save to the database */
	images: Image[];
	/** The keys of images we need to delete from both s3 + local */
	keysToDelete: string[];
	/** The keys of the images we need to save to local/remote */
	keysToSave: string[];
	/** An array of the imageBinaries from the local db */
	imageBinaries?: ImageBinary[];
	/** An array of revisions */
	revisions: Revision[];
	/**
	 * Ids of formTemplates that have instances in the donor data but for which the donor supplied no field templates.
	 * The merge continued using the receiver's copies; callers should tell the user the sync could not verify those templates.
	 */
	templateWarnings?: string[];
	/**
	 * Duplicate table / imageroll instances (same formInstance + fieldTemplateId) that this merge folded into one. The
	 * loser documents are emptied, never deleted. Empty or absent when nothing needed consolidating.
	 */
	consolidations?: TableConsolidation[];
}

/**
 * Finds formTemplates that have formInstances (with at least one fieldInstance carrying a fieldTemplateId) in the given
 * merge data, but for which the data holds zero fieldTemplates. That combination means the template data for this side
 * of the sync is incomplete, most likely because the fetch failed part way through.
 * @param data One side of the merge
 * @returns The ids of the formTemplates whose field templates are missing
 */
export function findTemplateGaps(data: MergeData): string[] {
	const formTemplateIdsWithFieldTemplates = new Set<string>();
	data.fieldTemplates.forEach((fieldTemplate) => {
		if (fieldTemplate.formTemplate) formTemplateIdsWithFieldTemplates.add(fieldTemplate.formTemplate);
	});
	const formInstanceIdToFormTemplateId = new Map<string, string>();
	data.formInstances.forEach((formInstance) => {
		if (formInstance.formTemplateId) formInstanceIdToFormTemplateId.set(formInstance.id, formInstance.formTemplateId);
	});
	const formTemplateIdsWithFieldInstances = new Set<string>();
	data.fieldInstances.forEach((fieldInstance) => {
		if (!fieldInstance.formInstance || !fieldInstance.fieldTemplateId) return;
		const formTemplateId = formInstanceIdToFormTemplateId.get(fieldInstance.formInstance);
		if (formTemplateId) formTemplateIdsWithFieldInstances.add(formTemplateId);
	});
	const gaps: string[] = [];
	formTemplateIdsWithFieldInstances.forEach((formTemplateId) => {
		if (!formTemplateIdsWithFieldTemplates.has(formTemplateId)) gaps.push(formTemplateId);
	});
	return gaps;
}

interface TemplateMergeDataToSave {
	/** The formTemplate documents we want to save to the database */
	formTemplates: FormTemplateForResolver[];
	/** The fieldTemplate documents we want to save to the database */
	fieldTemplates: FieldTemplateForResolver[];
	/** The option documents we want to save to the database */
	options: Option[];
	/** The fieldContinuationData documents we want to save to the database */
	continuationDatas: FieldContinuationDataForResolver[];
}

/**
 * Given the donor's data and receiver's data, handles the logic of selecting/merging each document type and returns the arrays to save to the db
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorData The donor's data
 * @param receiverData The receiver's data
 * @returns Arrays of the documents to save
 */
function merge(donorData: MergeData, receiverData: MergeData): MergeDataToSave {
	// Images, merge as before, but new function will be needed likely
	// merge user docs based on updatedAt. I realize we aren't updating user docs much at this point, but hey now if we do it'll just work
	// Okay kiddos, here's the dealio: we iterate through donor stuff, try to find it in receiver stuff. If receiver doesn't have it, we create it. If donor doesn't have something receiver
	// has, who gives a shit? Reciever has it already
	const projects = mergeAllThings(donorData.projects, receiverData.projects, createMergedProjectForPut);
	const formGroups = mergeAllThings(donorData.formGroups, receiverData.formGroups, createMergedFormGroup);
	const {
		finalContinuationDatas,
		finalFieldTemplates,
		finalFormTemplates,
		finalOptions,
		// finalSelectedFormTemplates,
		allProcessedFormTemplates,
		allFieldTemplates,
	} = handleMergingTemplates(donorData, receiverData);
	const formInstances = handleMergeFormInstances(donorData.formInstances, receiverData.formInstances, allProcessedFormTemplates);
	const mergedFieldInstances = handleMergeFieldInstances(donorData.fieldInstances, receiverData.fieldInstances, allFieldTemplates);
	// The per-instance merge above matches documents by id, so two instances for the same table on the same form both
	// survive it (and a stale device can hand the loser its rows back, because fieldsOrder merging is a union). Fold
	// them into the oldest one here, on every merge, so both sides converge on a single populated instance.
	const { fieldInstances, consolidations, skippedUnknownTemplate } = consolidateDuplicateTableInstances(mergedFieldInstances, (fieldTemplateId) => {
		const fieldTemplate = allFieldTemplates.get(fieldTemplateId);
		if (!fieldTemplate) return undefined;
		return fieldTemplate.inputType === "table" || fieldTemplate.inputType === "imageroll";
	});
	for (const consolidation of consolidations) {
		recordRepairBreadcrumb("merge-consolidation", { ...consolidation });
	}
	if (skippedUnknownTemplate > 0) {
		recordSentry({
			error: new Error(`Merge left ${skippedUnknownTemplate} duplicate field instance group(s) alone: field template not in the merge map`),
			msg: "Duplicate table instances skipped: template unknown",
			additionalData: { skippedUnknownTemplate },
		});
	}
	const revisions = handleMergeRevisions(donorData.revisions, receiverData.revisions);
	const { images, keysToDelete, keysToSave } = handleMergeImages(donorData.images, receiverData.images);
	return {
		projects,
		formGroups,
		formInstances,
		fieldInstances,
		consolidations,
		formTemplates: finalFormTemplates,
		fieldTemplates: finalFieldTemplates,
		options: finalOptions,
		continuationDatas: finalContinuationDatas,
		revisions: revisions.revisions,
		images,
		keysToDelete,
		keysToSave,
	};
}

/**
 * Given the donor's data and receiver's data, handles the logic of selecting/merging each template type and returns the arrays to save to the db
 * @author Robbie <rmartin@factorearth.com>
 * @param donorData The donor's data
 * @param receiverData The receiver's data
 * @returns Arrays of the documents to save
 */
export function mergeTemplates(donorData: TemplateMergeData, receiverData: TemplateMergeData): TemplateMergeDataToSave {
	// Images, merge as before, but new function will be needed likely
	// merge user docs based on updatedAt. I realize we aren't updating user docs much at this point, but hey now if we do it'll just work
	// Okay kiddos, here's the dealio: we iterate through donor stuff, try to find it in receiver stuff. If receiver doesn't have it, we create it. If donor doesn't have something receiver
	// has, who gives a shit? Reciever has it already
	const {
		finalContinuationDatas,
		finalFieldTemplates,
		finalFormTemplates,
		finalOptions,
		// finalSelectedFormTemplates,
		// allProcessedFormTemplates,
		// allFieldTemplates,
	} = handleMergingTemplates(donorData, receiverData);

	return {
		formTemplates: finalFormTemplates,
		fieldTemplates: finalFieldTemplates,
		options: finalOptions,
		continuationDatas: finalContinuationDatas,
	};
}

/**
 * Given the donor and receiver data, as well as the config saying where it's from/going, merges the data and saves it to the indicated database
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorData The donor's data
 * @param receiverData The receiver's data
 * @param syncConfig The config for merging everything correctly
 * @param snapshot Who is syncing; when given, the receiver's documents are uploaded to S3 before anything is written
 */
export async function mergeAndSave(
	donorData: MergeData,
	receiverData: MergeData,
	syncConfig: SyncConfig,
	snapshot?: { username?: string },
): Promise<MergeDataToSave> {
	// Guard against merging instances whose templates we could not load. If the donor has no field templates for a form
	// that has instances, the receiver's copies are used (see handleMergingTemplates) and the caller is told. If neither
	// side has them, no correct merge is possible, so stop before anything is written.
	const donorGaps = findTemplateGaps(donorData);
	const receiverGaps = findTemplateGaps(receiverData);
	const unresolvableGaps = donorGaps.filter((id) => receiverGaps.includes(id));
	if (unresolvableGaps.length > 0) {
		const error = new Error(`Sync aborted: no field templates on either side for formTemplates ${unresolvableGaps.join(", ")}`);
		recordSentry({
			error,
			msg: "Sync aborted: field templates missing on both sides",
			additionalData: { source: syncConfig.source, destination: syncConfig.destination, unresolvableGaps },
		});
		throw error;
	}
	if (donorGaps.length > 0) {
		recordSentry({
			error: new Error(`Donor (${syncConfig.source}) supplied no field templates for formTemplates ${donorGaps.join(", ")}; receiver copies used`),
			msg: "Sync continued with receiver field templates",
			additionalData: { source: syncConfig.source, destination: syncConfig.destination, donorGaps },
		});
	}

	// The side about to be overwritten is snapshotted exactly as this merge saw it. The other side already has its
	// own tree backup (sync up) or is the cloud (sync down). Never blocks the sync.
	if (snapshot) await uploadMergeInputSnapshot(receiverData, donorData, syncConfig, snapshot.username);

	const mergeDataToSave = merge(donorData, receiverData);
	if (donorGaps.length > 0) mergeDataToSave.templateWarnings = donorGaps;

	// When syncing down to local, ensure any image whose binary is missing offline is queued for download.
	if (syncConfig.destination === "local") {
		const candidateKeys = mergeDataToSave.images.map((img) => img.key).filter((key): key is string => typeof key === "string" && key.length > 0);
		if (candidateKeys.length > 0) {
			const existingBinaries = await bulkGetImageBinaries(candidateKeys);
			const existingKeys = new Set(existingBinaries.map((b) => b.key));
			for (const key of candidateKeys) {
				if (!existingKeys.has(key) && !mergeDataToSave.keysToSave.includes(key)) {
					mergeDataToSave.keysToSave.push(key);
				}
			}
		}
	}

	await saveStuff(mergeDataToSave, syncConfig);
	return mergeDataToSave;
}

/**
 * Uploads the receiver's merge input to S3 next to the sync-up tree backups, as
 * `{group}/sync-backups/{projectName}/{username}-{HHmmss MM-DD-YY}-merge-input-{destination}.json`. Errors are
 * recorded and swallowed: a missing snapshot must never stop a sync.
 */
async function uploadMergeInputSnapshot(receiverData: MergeData, donorData: MergeData, syncConfig: SyncConfig, username?: string) {
	try {
		const project = receiverData.projects.values().next().value ?? donorData.projects.values().next().value;
		const group = project?.group ?? receiverData.formGroups.values().next().value?.group ?? donorData.formGroups.values().next().value?.group;
		if (!group) return;
		const date = new Date();
		const pad = (value: number) => value.toString().padStart(2, "0");
		const finalTime = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())} ${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date
			.getFullYear()
			.toString()
			.slice(-2)}`;
		const key = `${group}/sync-backups/${project?.name ?? "UnknownProject"}/${username || "UnknownUser"}-${finalTime}-merge-input-${
			syncConfig.destination
		}.json`;
		const data = JSON.stringify({
			source: syncConfig.source,
			destination: syncConfig.destination,
			receiver: serializeMergeData(receiverData as unknown as Record<string, unknown>),
		});
		await uploadData({ key, data }).result;
	} catch (error) {
		recordSentry({
			error,
			msg: "merge-input snapshot failed; sync continued",
			additionalData: { source: syncConfig.source, destination: syncConfig.destination },
		});
	}
}

/**
 * Given the donor and receiver data, as well as the config saying where it's from/going, merges the data and saves it to the indicated database
 * @author Robbie <rmartin@factorearth.com>
 * @param donorData The donor's data
 * @param receiverData The receiver's data
 * @param syncConfig The config for merging everything correctly
 * @param shouldSavePhotos
 */
export async function mergeAndSaveTemplates(
	donorData: TemplateMergeData,
	receiverData: TemplateMergeData,
	syncConfig: SyncConfig,
): Promise<TemplateMergeDataToSave> {
	const mergeDataToSave = mergeTemplates(donorData, receiverData);
	await saveTemplates(mergeDataToSave, syncConfig);
	return mergeDataToSave;
}

/**
 * Given the things from both the donor and receiver, loops through the donor's things, finds it in the receiver's things, and merges if it does (or just saving it otherwise)
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorProjects The map of things the donor has
 * @param receivierProjects The map of things the receiver has
 * @returns An array of things ready for the db. Could be merged or saved
 */
function mergeAllThings<T>(donorThings: Map<string, T>, receiverThings: Map<string, T>, mergeFunction: (donorThing: T, receiverThing: T) => T): T[] {
	const finalThings: T[] = [];
	donorThings.forEach((thing, id) => {
		const receiverThing = receiverThings.get(id);
		if (receiverThing) {
			// Merge
			const mergedThing = mergeFunction(thing, receiverThing);
			finalThings.push(mergedThing);
		} else {
			// Save
			finalThings.push(thing);
		}
		// We need to make sure we have all things returned so that we can sync back down on the off chance this is a sync up. For that reason, delete this id from receiver, so we can loop
		// through receiver stuff after and have only the stuff that's there. Clear as mud?
		receiverThings.delete(id);
	});
	receiverThings.forEach((thing) => {
		finalThings.push(thing);
	});
	return finalThings;
}

/**
 * Given the donor & receiver data, goes through and determins which it needs, and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorData The donor's data
 * @param receiverData The receiver's data
 * @returns All templates that need saved, as well as the selected formTemplates and fieldTemplates
 */
function handleMergingTemplates(donorData: TemplateMergeData, receiverData: TemplateMergeData): TemplateMergeResult {
	const finalFormTemplates: FormTemplateForResolver[] = [];
	const finalFieldTemplates: FieldTemplateForResolver[] = [];
	const finalOptions: Option[] = [];
	const finalContinuationDatas: FieldContinuationDataForResolver[] = [];
	const finalSelectedFormTemplates: Map<string, FormTemplateForResolver> = new Map(); // Only templates that need saving
	const allProcessedFormTemplates: Map<string, FormTemplateForResolver> = new Map(); // All templates for field instance processing
	const allFieldTemplates: Map<string, FieldTemplateForResolver> = new Map();

	donorData.formTemplates.forEach((donorFormTemplate, id) => {
		// Merge
		const receiverFormTemplate = receiverData.formTemplates.get(id);
		if (receiverFormTemplate) {
			const mergedFormTemplate = createMergedFormTemplate(donorFormTemplate, receiverFormTemplate);

			// Always track the merged template for field instance processing
			allProcessedFormTemplates.set(mergedFormTemplate.id, mergedFormTemplate);

			// Only add to final arrays if the merged template is actually different from what's in receiver
			const receiverTimestamp = new Date(receiverFormTemplate.updatedAt).getTime();
			const mergedTimestamp = new Date(mergedFormTemplate.updatedAt).getTime();

			if (mergedTimestamp > receiverTimestamp || mergedFormTemplate.id !== receiverFormTemplate.id) {
				finalFormTemplates.push(mergedFormTemplate);
				finalSelectedFormTemplates.set(mergedFormTemplate.id, mergedFormTemplate);
			}
		} else {
			// Save - receiver doesn't have this template
			const donorCopy = createFormTemplateForBatchResolver(donorFormTemplate);
			finalFormTemplates.push(donorCopy);
			finalSelectedFormTemplates.set(donorCopy.id, { ...donorCopy });
			allProcessedFormTemplates.set(donorCopy.id, { ...donorCopy });
		}
		// We need to make sure we have all things returned so that we can sync back down on the off chance this is a sync up. For that reason, delete this id from receiver, so we can loop
		// through receiver stuff after and have only the stuff that's there. Clear as mud?
		receiverData.formTemplates.delete(id);
	});
	// Track receiver-only templates for field instance processing (but don't save them)
	receiverData.formTemplates.forEach((formTemplate) => {
		const wasProcessedAbove = donorData.formTemplates.has(formTemplate.id);
		if (!wasProcessedAbove) {
			// This template exists only in receiverData - track it for field instance processing
			allProcessedFormTemplates.set(formTemplate.id, formTemplate);
		}
	});

	donorData.fieldTemplates.forEach((donorFieldTemplate, id) => {
		const selectedFormTemplate = allProcessedFormTemplates.get(donorFieldTemplate.formTemplate);

		if (selectedFormTemplate) {
			// Check if the selected form template came from donor data (by comparing with donorData)
			const donorFormTemplate = donorData.formTemplates.get(donorFieldTemplate.formTemplate);
			const donorWasSelected =
				donorFormTemplate && selectedFormTemplate.updatedAt === donorFormTemplate.updatedAt && selectedFormTemplate.id === donorFormTemplate.id;

			if (donorWasSelected) {
				// If donor form template was selected, use ALL donor children without merging
				const donorCopy = createFieldTemplateForBatchResolver(donorFieldTemplate);
				finalFieldTemplates.push(donorCopy);
				allFieldTemplates.set(donorCopy.id, donorCopy);

				if (donorCopy.continuationDataId) {
					const donorContData = donorData.continuationDatas.get(donorCopy.continuationDataId);
					if (donorContData) {
						finalContinuationDatas.push(donorContData);
					}
				}

				if (donorCopy.optionsOrder) {
					for (const optionId of donorCopy.optionsOrder) {
						const donorOption = donorData.options.get(optionId);
						if (donorOption) finalOptions.push(donorOption);
					}
				}
			} else {
				// Receiver form template was selected, do individual merging
				const receiverFieldTemplate = receiverData.fieldTemplates.get(id);
				if (receiverFieldTemplate) {
					// Both the donor and receiver have the fieldTemplate. DO merge logic to check and see if one has been updated
					const mergedFieldTemplate = createMergedFieldTemplateForBatchResolver(donorFieldTemplate, receiverFieldTemplate);
					finalFieldTemplates.push(mergedFieldTemplate);
					allFieldTemplates.set(mergedFieldTemplate.id, mergedFieldTemplate);
					if (mergedFieldTemplate.continuationDataId) {
						const donorContData = donorData.continuationDatas.get(mergedFieldTemplate.continuationDataId);
						const receiverContData = receiverData.continuationDatas.get(mergedFieldTemplate.continuationDataId);
						if (donorContData && receiverContData) {
							// Merge the 2
							const mergedContData = createMergedFieldContinuationData(donorContData, receiverContData);
							finalContinuationDatas.push(mergedContData);
						} else if (donorContData) {
							// The donor has a continuation data, and the donor fieldTemplate is newer, so we need this cont data. just save it
							finalContinuationDatas.push(donorContData);
						} else if (receiverContData) {
							finalContinuationDatas.push(receiverContData);
						}
					}
					if (mergedFieldTemplate.optionsOrder) {
						for (const optionId of mergedFieldTemplate.optionsOrder) {
							const donorOption = donorData.options.get(optionId);
							const receiverOption = receiverData.options.get(optionId);
							if (donorOption && receiverOption) {
								// Both have the option, merge them
								const chosenOption = createMergedOptionForBatchResolver(donorOption, receiverOption);
								finalOptions.push(chosenOption);
							} else if (donorOption) {
								finalOptions.push(donorOption);
							} else if (receiverOption) {
								finalOptions.push(receiverOption);
							}
						}
					}
				} else {
					// Only the donor has it, so just save it
					const donorCopy = createFieldTemplateForBatchResolver(donorFieldTemplate);
					finalFieldTemplates.push(donorCopy);
					allFieldTemplates.set(donorCopy.id, { ...donorCopy });
					if (donorCopy.continuationDataId) {
						const donorContData = donorData.continuationDatas.get(donorCopy.continuationDataId);
						if (donorContData) {
							finalContinuationDatas.push(donorContData);
						}
					}
					if (donorCopy.optionsOrder) {
						for (const optionId of donorCopy.optionsOrder) {
							const donorOption = donorData.options.get(optionId);
							if (donorOption) finalOptions.push(donorOption);
						}
					}
				}
			}
		}
		// We need to make sure we have all things returned so that we can sync back down on the off chance this is a sync up. For that reason, delete this id from receiver, so we can loop
		// through receiver stuff after and have only the stuff that's there. Clear as mud?
		receiverData.fieldTemplates.delete(id);
	});

	receiverData.fieldTemplates.forEach((fieldTemplate) => {
		// Only add fieldTemplates whose parent FormTemplate is available for processing
		const parentFormTemplateIsAvailable = allProcessedFormTemplates.has(fieldTemplate.formTemplate);

		if (parentFormTemplateIsAvailable) {
			// Check if the selected FormTemplate came from donor or receiver to use consistent data
			const selectedFormTemplate = allProcessedFormTemplates.get(fieldTemplate.formTemplate);
			const donorFormTemplate = donorData.formTemplates.get(fieldTemplate.formTemplate);
			const donorWasSelected = donorFormTemplate && selectedFormTemplate && selectedFormTemplate.updatedAt === donorFormTemplate.updatedAt;

			// Regardless of which side's FormTemplate was selected, every field template the receiver knows about must be
			// visible to field-instance processing. If the donor's template fetch came back empty (a failed request during a
			// sync), the donor is still "selected" above because the form templates match, and without this line
			// handleMergeFieldInstances would find no template for any instance and strip their fieldTemplateIds.
			// This only affects the lookup map; what gets saved is still decided by donorWasSelected below.
			if (!allFieldTemplates.has(fieldTemplate.id)) {
				allFieldTemplates.set(fieldTemplate.id, createFieldTemplateForBatchResolver(fieldTemplate));
			}

			if (!donorWasSelected) {
				// Receiver FormTemplate was selected, use receiver data consistently
				const receiverCopy = createFieldTemplateForBatchResolver(fieldTemplate);
				finalFieldTemplates.push(receiverCopy);
				allFieldTemplates.set(receiverCopy.id, { ...receiverCopy });

				if (receiverCopy.continuationDataId) {
					const receiverContData = receiverData.continuationDatas.get(receiverCopy.continuationDataId);
					if (receiverContData) {
						finalContinuationDatas.push(receiverContData);
					}
				}
				if (receiverCopy.optionsOrder) {
					for (const optionId of receiverCopy.optionsOrder) {
						const receiverOption = receiverData.options.get(optionId);
						if (receiverOption) finalOptions.push(receiverOption);
					}
				}
			}
		}
	});

	return {
		finalContinuationDatas,
		finalFieldTemplates,
		finalFormTemplates,
		finalOptions,
		finalSelectedFormTemplates,
		allProcessedFormTemplates,
		allFieldTemplates,
	};
}

/**
 * Given the donor & receiver forms, merges/saves them as needed with the correct formTemplateId and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorForms The donor's forms
 * @param receiverForms The receiver's forms
 * @param savedFormTemplates The formTemplates we know we need
 * @returns The formInstances we need
 */
function handleMergeFormInstances(
	donorForms: Map<string, FormInstanceForResolver>,
	receiverForms: Map<string, FormInstanceForResolver>,
	savedFormTemplates: Map<string, FormTemplateForResolver>,
): FormInstanceForResolver[] {
	const finalFormInstances: FormInstanceForResolver[] = [];
	donorForms.forEach((donorForm, id) => {
		const receiverForm = receiverForms.get(id);
		if (receiverForm) {
			// Merge them, potentially change over formTemplates
			const donorFormTemplate = savedFormTemplates.get(donorForm.formTemplateId);
			const receiverFormTemplate = savedFormTemplates.get(receiverForm.formTemplateId);

			if (donorFormTemplate && receiverFormTemplate) {
				const mergedForm = createMergedFormInstance(donorForm, receiverForm, donorFormTemplate, receiverFormTemplate);
				finalFormInstances.push(mergedForm);
			} else {
				// If the templates aren't both there, I'm really not sure what to do

				recordSentry({
					error: "MERGING FORM INSTANCES, WE COULDN'T FIND BOTH FORM TEMPLATES",
					msg: "MERGING FORM INSTANCES, WE COULDN'T FIND BOTH FORM TEMPLATES",
				});
			}
		} else {
			// Only donor has it, so save it

			const donorCopy = createFormInstanceForBatchResolver(donorForm);
			finalFormInstances.push(donorCopy);
		}
		receiverForms.delete(id);
	});
	receiverForms.forEach((form) => {
		const receiverCopy = createFormInstanceForBatchResolver(form);
		finalFormInstances.push(receiverCopy);
	});

	return finalFormInstances;
}

/**
 * Given the donor/receiver fields, saves/merges them as needed and outputs an array of the final fieldInstances to be saved
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFields The fields coming from the donor's data
 * @param receiverFields The fields that the receiver has
 * @param savedFormTemplates The formTemplates we elected to save earlier
 * @param savedFieldTemplates The fieldTemplates we elected to save earlier
 * @returns The fields ready to be saved to the db
 */
function handleMergeFieldInstances(
	donorFields: Map<string, FieldInstanceForResolver>,
	receiverFields: Map<string, FieldInstanceForResolver>,
	savedFieldTemplates: Map<string, FieldTemplateForResolver>,
): FieldInstanceForResolver[] {
	const finalFields: FieldInstanceForResolver[] = [];
	donorFields.forEach((donorField, id) => {
		const receiverField = receiverFields.get(id);
		if (receiverField) {
			// Both a donor and receiver, that means merge time
			const mergedField = createMergedFieldForBatchResolver(donorField, receiverField, savedFieldTemplates);
			finalFields.push(mergedField);
		} else {
			// Only the donor exists, so we're just saving. No Merge/Conversion
			const donorCopy = createFieldForBatchResolver(donorField);
			finalFields.push(donorCopy);
		}
		receiverFields.delete(id);
	});
	receiverFields.forEach((receiverField) => {
		const receiverCopy = createFieldForBatchResolver(receiverField);
		finalFields.push(receiverCopy);
	});
	return finalFields;
}

/**
 * Given the donor + receiver docs and the sync config, gets the final images to save and returns them and the s3 keys to delete
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorImages The donor image docs
 * @param receiverImages The receiver image docs
 * @param syncConfig The config showing what's being saved where
 * @returns The image docs to save, and the keys to delete
 */
function handleMergeImages(
	donorImages: Map<string, Image>,
	receiverImages: Map<string, Image>,
): { images: Image[]; keysToDelete: string[]; keysToSave: string[] } {
	const finalImages: Image[] = [];
	const keysToDelete: string[] = [];
	const keysToSave: string[] = [];
	donorImages.forEach((donorImage, id) => {
		const receiverImage = receiverImages.get(donorImage.id);
		if (receiverImage) {
			// We gotta merge this
			const mergeResult = createdMergedImageDocForResolver(donorImage, receiverImage);
			finalImages.push(mergeResult.image);
			if (mergeResult.keyToDelete) {
				keysToDelete.push(mergeResult.keyToDelete);
			}
			if (mergeResult.keyToSave) {
				keysToSave.push(mergeResult.keyToSave);
			}
		} else {
			// New Image - only in donor, receiver doesn't have it
			finalImages.push(donorImage);
			// Just a typescript satisfier, it should be a string here
			if (donorImage.key && typeof donorImage.key === "string") keysToSave.push(donorImage.key);
		}
		receiverImages.delete(id);
	});
	receiverImages.forEach((receiverImage) => {
		finalImages.push(receiverImage);
	});
	return { images: finalImages, keysToDelete, keysToSave };
}

function handleMergeRevisions(donorRevisions: Map<string, Revision>, receiverRevisions: Map<string, Revision>): { revisions: Revision[] } {
	// This basically needs to create an array of revisions that are unique between the two maps.
	const newRevisions = [];
	const donorArray = [...donorRevisions.values()];
	for (let i = 0; i < donorArray.length; i++) {
		i;
		const donorRevision = donorArray[i];
		const isInReceiver = receiverRevisions.has(donorRevision.id);
		if (!isInReceiver) {
			newRevisions.push(donorRevision);
		}
	}

	return { revisions: newRevisions };
}

/**
 * Given the json and instructions for where to save it, saves the data to the database
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonStructure The data from the json
 * @param syncConfig The config telling us where the data is coming from/going to
 */
export async function saveJsonDirectlyToDb(jsonStructure: JsonStructure, syncConfig: SyncConfig): Promise<void> {
	// Use the new merge-aware version instead of direct conversion
	await saveJsonWithMergeLogic(jsonStructure, syncConfig);
}

/**
 * Saves JSON data to the database with proper template timestamp checking and merging logic
 * @param jsonStructure The JSON structure to save
 * @param syncConfig The sync configuration
 */
export async function saveJsonWithMergeLogic(jsonStructure: JsonStructure, syncConfig: SyncConfig): Promise<void> {
	// Convert JSON to MergeData format for comparison
	const donorData = convertJsonToMergeData(jsonStructure);
	const receiverData: MergeData = createEmptyMergeData();

	// Fetch existing templates from the same location we plan to save to for proper comparison
	const fetchFromRemote = syncConfig.destination === "remote";
	await addAllFormTemplatesToMergeData(receiverData, donorData, fetchFromRemote);

	// Perform the merge operation (only newer templates will be included)
	const mergeDataToSave = merge(donorData, receiverData);

	// Save the merged result
	await saveStuff(mergeDataToSave, syncConfig);
}

/**
 * Converts JSON structure to MergeData format for comparison
 * @param json The JSON structure to convert
 * @returns MergeData object
 */
function convertJsonToMergeData(json: JsonStructure): MergeData {
	const projects = new Map<string, ProjectForResolver>();
	const formGroups = new Map<string, FormGroupForResolver>();
	const formInstances = new Map<string, FormInstanceForResolver>();
	const fieldInstances = new Map<string, FieldInstanceForResolver>();
	const formTemplates = new Map<string, FormTemplateForResolver>();
	const fieldTemplates = new Map<string, FieldTemplateForResolver>();
	const options = new Map<string, Option>();
	const continuationDatas = new Map<string, FieldContinuationDataForResolver>();
	const images = new Map<string, Image>();
	const users = new Map<string, UserForResolver>();
	const revisions = new Map<string, Revision>();

	// Use resolver functions to ensure proper format conversion
	json.projects.forEach((project) => projects.set(project.id, createProjectForPut(project)));
	json.formGroups.forEach((fg) => formGroups.set(fg.id, createFormGroupForBatchResolver(fg)));
	json.formInstances.forEach((fi) => formInstances.set(fi.id, createFormInstanceForBatchResolver(fi)));
	json.fieldInstances.forEach((field) => fieldInstances.set(field.id, createFieldForBatchResolver(field)));
	json.formTemplates.forEach((template) => formTemplates.set(template.id, createFormTemplateForBatchResolver(template)));
	json.fieldTemplates.forEach((template) => fieldTemplates.set(template.id, createFieldTemplateForBatchResolver(template)));
	json.options.forEach((option) => options.set(option.id, createOptionForBatchResolver(option)));
	json.continuationDatas.forEach((cd) => continuationDatas.set(cd.id, createFieldContinuationDataForResolver(cd)));
	json.images.forEach((image) => images.set(image.id, createImageForBatchResolver(image)));
	json.revisions.forEach((revision) => revisions.set(revision.id, createRevisionForBatchResolver(revision)));

	return {
		projects,
		formGroups,
		formInstances,
		fieldInstances,
		formTemplates,
		fieldTemplates,
		options,
		continuationDatas,
		images,
		revisions,
		users,
	};
}

/**
 * Creates an empty MergeData object
 * @returns Empty MergeData object
 */
function createEmptyMergeData(): MergeData {
	return {
		projects: new Map(),
		formGroups: new Map(),
		formInstances: new Map(),
		fieldInstances: new Map(),
		formTemplates: new Map(),
		fieldTemplates: new Map(),
		options: new Map(),
		continuationDatas: new Map(),
		images: new Map(),
		revisions: new Map(),
		users: new Map(),
	};
}

/**
 * Given the final stuff to save and the sync config, saves everything to where it needs to be saved
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The stuff to save
 * @param syncConfig The config showing what's going where
 */
async function saveStuff(mergeDataToSave: MergeDataToSave, syncConfig: SyncConfig): Promise<void> {
	await saveToPlace(mergeDataToSave, syncConfig.destination, syncConfig.source, syncConfig.online, syncConfig.savePhotos);
	// This is just taking all the updated data and saving it to the source so it's completely synced. We've decided for now for that to only affect the offline => online sync
	if (syncConfig.destination === "remote" && syncConfig.source === "local") {
		await saveToPlace(mergeDataToSave, syncConfig.source, syncConfig.destination, syncConfig.online, false);
	}
}

/**
 * Given the final stuff to save and the sync config, saves everything to where it needs to be saved template style
 * @author Robbie <rmartin@factorearth.com>
 * @param mergeDataToSave The stuff to save
 * @param syncConfig The config showing what's going where
 */
async function saveTemplates(mergeDataToSave: TemplateMergeDataToSave, syncConfig: SyncConfig): Promise<void> {
	await saveTemplatesToPlace(mergeDataToSave, syncConfig.destination, syncConfig.source, syncConfig.online);
	// This is just taking all the updated data and saving it to the source so it's completely synced. We've decided for now for that to only affect the offline => online sync
	if (syncConfig.destination === "remote" && syncConfig.source === "local") {
		await saveTemplatesToPlace(mergeDataToSave, syncConfig.source, syncConfig.destination, syncConfig.online);
	}
}

/**
 * Given the data to save and a location, we save the data to that location
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The "correct" data that needs to be saved to the db
 * @param destination The location we need to save the data to
 * @param source The source of the merge
 * @param online Whether or not the user is online
 */
async function saveToPlace(mergeDataToSave: MergeDataToSave, destination: Source, source: Source, online: boolean, savePhotosOffline?: boolean): Promise<void> {
	switch (destination) {
		case "json":
			// If we ever have bi-directional merging, it's possible it comes from json, and if it does, we don't want to do anything here. The downloadFormGroupJson should be handling that
			break;
		case "local":
			await saveOffline(mergeDataToSave, source, online, savePhotosOffline);
			break;
		case "remote":
			await saveOnline(mergeDataToSave);
			break;
		default:
			console.error(`UNKNOWN LOCATION, CANNOT SAVE DATA: ${destination}`);
	}
}

/**
 * Given the data to save and a location, we save the data to that location
 * @author Robbie <rmartin@factorearth.com>
 * @param mergeDataToSave The "correct" data that needs to be saved to the db
 * @param destination The location we need to save the data to
 * @param source The source of the merge
 * @param online Whether or not the user is online
 */
async function saveTemplatesToPlace(mergeDataToSave: TemplateMergeDataToSave, destination: Source, source: Source, online: boolean): Promise<void> {
	switch (destination) {
		case "json":
			// If we ever have bi-directional merging, it's possible it comes from json, and if it does, we don't want to do anything here. The downloadFormGroupJson should be handling that
			break;
		case "local":
			await saveTemplatesOffline(mergeDataToSave, source, online);
			break;
		case "remote":
			await saveTemplatesOnline(mergeDataToSave);
			break;
		default:
			console.error(`UNKNOWN LOCATION, CANNOT SAVE DATA: ${destination}`);
	}
}

/**
 * Given the correct data to save, saves it to the indexeddb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The "correct" data that needs to be saved
 * @param source The source of the merge
 * @param boolean Whether or not the user is online
 */
async function saveOffline(mergeDataToSave: MergeDataToSave, source: Source, online: boolean, savePhotosOffline?: boolean): Promise<void> {
	const promises = [
		bulkSaveProjects(mergeDataToSave.projects),
		bulkSaveFormGroupDocs(mergeDataToSave.formGroups),
		bulkSaveFormInstances(mergeDataToSave.formInstances),
		bulkSaveFieldInstances(mergeDataToSave.fieldInstances),
		bulkSaveFormTemplates(mergeDataToSave.formTemplates),
		bulkSaveFieldTemplates(mergeDataToSave.fieldTemplates),
		bulkSaveOptions(mergeDataToSave.options),
		bulkSaveFieldContinuationDatas(mergeDataToSave.continuationDatas),
		bulkSaveImageDocs(mergeDataToSave.images),
		putRevisions(mergeDataToSave.revisions, false),
	];
	// Run core document saves first.
	await Promise.all(promises);

	// Then delete stale binaries.
	await bulkDeleteImageBinaryDocs(mergeDataToSave.keysToDelete);

	// Finally, download binaries selected for offline use.
	if (online && source === "remote" && savePhotosOffline) {
		await getAllImagesFromOnlineAndSaveToBinary(mergeDataToSave.keysToSave);
	}
}

/**
 * Given the correct data to save, saves it to the indexeddb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The "correct" data that needs to be saved
 * @param source The source of the merge
 * @param boolean Whether or not the user is online
 */
async function saveTemplatesOffline(mergeDataToSave: TemplateMergeDataToSave, source: Source, online: boolean, savePhotosOffline?: boolean): Promise<void> {
	const promises = [
		bulkSaveFormTemplates(mergeDataToSave.formTemplates),
		bulkSaveFieldTemplates(mergeDataToSave.fieldTemplates),
		bulkSaveOptions(mergeDataToSave.options),
		bulkSaveFieldContinuationDatas(mergeDataToSave.continuationDatas),
	];
	await Promise.all(promises);
}

/**
 * Given the correct data to save, saves it all to the dynamodb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The "correct" data to save
 * @param source The source of the merge
 * @param boolean Whether or not the user is online
 */
async function saveOnline(mergeDataToSave: MergeDataToSave): Promise<void> {
	// Should be able to promise.all this because they're hitting different tables and therefore not contributing to throughput issues
	await Promise.all([
		handleBatchCrud(mergeDataToSave.projects, batchCreateProjects, 25),
		handleBatchCrud(mergeDataToSave.formGroups, batchCreateFormGroups, 25),
		handleBatchCrud(mergeDataToSave.formInstances, batchCreateFormInstances, 25),
		handleBatchCrud(mergeDataToSave.fieldInstances, batchCreateFieldInstances, 25),
		handleBatchCrud(mergeDataToSave.formTemplates, batchCreateFormTemplates, 25),
		handleBatchCrud(mergeDataToSave.fieldTemplates, batchCreateFieldTemplates, 25),
		handleBatchCrud(mergeDataToSave.options, batchCreateOptions, 25),
		putRevisions(mergeDataToSave.revisions, true),
		handleBatchCrud(mergeDataToSave.continuationDatas, batchCreateFieldContinuationData, 25),
		deleteImagesFromS3(mergeDataToSave.keysToDelete),
		getAllImagesFromOfflineAndSaveToS3(mergeDataToSave.keysToSave, mergeDataToSave.images),
	]);
}

/**
 * Given the correct data to save, saves it all to the dynamodb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param mergeDataToSave The "correct" data to save
 * @param source The source of the merge
 * @param boolean Whether or not the user is online
 */
async function saveTemplatesOnline(mergeDataToSave: TemplateMergeDataToSave): Promise<void> {
	// Should be able to promise.all this because they're hitting different tables and therefore not contributing to throughput issues
	await Promise.all([
		handleBatchCrud(mergeDataToSave.formTemplates, batchCreateFormTemplates, 25),
		handleBatchCrud(mergeDataToSave.fieldTemplates, batchCreateFieldTemplates, 25),
		handleBatchCrud(mergeDataToSave.options, batchCreateOptions, 25),
		handleBatchCrud(mergeDataToSave.continuationDatas, batchCreateFieldContinuationData, 25),
	]);
}

/**
 * Given the json, converts it directly to mergeDataToSave to skip most of the logic. Used for restoring a project from json
 * @author Eric Webb <ewebb@factorearth.com>
 * @param json The json
 * @returns The json but as MergeDataToSave
 */
// function convertJsonToMergeDataToSave(json: JsonStructure): MergeDataToSave {
// 	// Calling the createXXXForResolver functions to parse the uploaded data from the user
// 	const mergeDataToSave: MergeDataToSave = {
// 		projects: json.projects.map((proj) => createProjectForPut(proj)),
// 		formGroups: json.formGroups.map((fg) => createFormGroupForBatchResolver(fg)),
// 		formInstances: json.formInstances.map((formInstance) => createFormInstanceForBatchResolver(formInstance)),
// 		fieldInstances: json.fieldInstances.map((fieldInstance) => createFieldForBatchResolver(fieldInstance)),
// 		formTemplates: json.formTemplates.map((formTemplate) => createFormTemplateForBatchResolver(formTemplate)),
// 		fieldTemplates: json.fieldTemplates.map((fieldTemplate) => createFieldTemplateForBatchResolver(fieldTemplate)),
// 		options: json.options.map((option) => createOptionForBatchResolver(option)),
// 		continuationDatas: json.continuationDatas.map((cont) => createFieldContinuationDataForResolver(cont)),
// 		images: json.images.map((img) => createImageForBatchResolver(img)),
// 		revisions: json.revisions.map((rev) => createRevisionForBatchResolver(rev)),
// 		keysToDelete: [],
// 		keysToSave: [],
// 	};
// 	return mergeDataToSave;
// }

/**
 * Given a json upload, changes the ids and references for every instance and returns the json structure. Used for uploading stuff from other projects into this project
 * @author Eric Webb <ewebb@factorearth.com>
 * @param json The json that's being uploaded
 * @param projectId The id of the project we're trying to upload to
 * @returns A json structure where all instances have new ids and their references are corrected
 */
export function duplicateJsonStructure(json: JsonStructure, projectId: string): JsonStructure {
	// Change formGroup ids and store the relation from old to new
	const oldFormGroupIdToNewFormGroupId = new Map<string, string>();
	const oldFormInstanceIdToNewFormInstanceId = new Map<string, string>();
	const formGroups = copyFormGroups(json.formGroups, oldFormGroupIdToNewFormGroupId, projectId, oldFormInstanceIdToNewFormInstanceId);

	// Change formInstance ids and store the relation from old to new
	const formInstances = copyFormInstances(json.formInstances, oldFormInstanceIdToNewFormInstanceId, oldFormGroupIdToNewFormGroupId);

	// Change image ids and store the relation from old to new
	const oldImageIdToNewImageId = new Map<string, string>();
	const images = copyImages(json.images, oldImageIdToNewImageId, oldFormGroupIdToNewFormGroupId, projectId);

	// Change fieldInstance ids and store the relation from old to new
	const oldFieldInstanceIdToNewFieldInstanceId = new Map<string, string>();
	const fieldInstances = copyFieldInstances(
		json.fieldInstances,
		oldFieldInstanceIdToNewFieldInstanceId,
		oldFormInstanceIdToNewFormInstanceId,
		oldFormGroupIdToNewFormGroupId,
		oldImageIdToNewImageId,
	);

	// Change revision ids
	const revisions = copyRevisions(json.revisions, oldFieldInstanceIdToNewFieldInstanceId, oldFormGroupIdToNewFormGroupId);

	const newJson: JsonStructure = {
		projects: [],
		formGroups,
		formInstances,
		fieldInstances,
		formTemplates: json.formTemplates,
		fieldTemplates: json.fieldTemplates,
		options: json.options,
		continuationDatas: json.continuationDatas,
		images,
		revisions,
		version: CURRENT_JSON_VERSION,
	};
	return newJson;
}

/**
 * Given the old formGroups, loops through them, giving them a new id and changing the references to their projects and formInstances
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonFormGroups The formGroups from the json
 * @param oldFormGroupIdToNewFormGroupId A map for setting the old id to a newly generated id
 * @param projectId The new projectId
 * @param oldFormInstanceIdToNewFormInstanceId A map for setting the old formInstance ids to new ids, for the purpose of updating formOrder
 * @returns The formGroups with new ids and updated references
 */
function copyFormGroups(
	jsonFormGroups: FormGroupForResolver[],
	oldFormGroupIdToNewFormGroupId: Map<string, string>,
	projectId: string,
	oldFormInstanceIdToNewFormInstanceId: Map<string, string>,
): FormGroupForResolver[] {
	const formGroups = jsonFormGroups.map((formGroup) => {
		const newFormGroupId = uuid();
		oldFormGroupIdToNewFormGroupId.set(formGroup.id, newFormGroupId);
		formGroup.id = newFormGroupId;
		formGroup.projectId = projectId;
		formGroup.formOrder = formGroup.formOrder.map((oldFormId) => {
			const newFormId = uuid();
			oldFormInstanceIdToNewFormInstanceId.set(oldFormId, newFormId);
			return newFormId;
		});
		return formGroup;
	});
	return formGroups;
}

/**
 * Given the old formInstances, loops through them, giving them new ids and changing the references to their formGroups
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonFormInstances The formInstances from the json
 * @param oldFormInstanceIdToNewFormInstanceId A map for setting the old id to a newly generated one
 * @param oldFormGroupIdToNewFormGroupId The map populated in {@link copyFormGroups} that has the old formGroup ids to their new ids
 * @returns The formInstances with new ids and updated references
 */
function copyFormInstances(
	jsonFormInstances: FormInstanceForResolver[],
	oldFormInstanceIdToNewFormInstanceId: Map<string, string>,
	oldFormGroupIdToNewFormGroupId: Map<string, string>,
): FormInstanceForResolver[] {
	const formInstances = jsonFormInstances.map((formInstance) => {
		const newFormInstanceId = oldFormInstanceIdToNewFormInstanceId.get(formInstance.id);
		if (newFormInstanceId) formInstance.id = newFormInstanceId;
		const formGroupId = oldFormGroupIdToNewFormGroupId.get(formInstance.formGroup);
		if (formGroupId) formInstance.formGroup = formGroupId;
		return formInstance;
	});
	return formInstances;
}

/**
 * Given the old images, loops through them, giving them new ids and updating their references to the formGroup and project
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonImages The images from the json
 * @param oldImageIdToNewImageId A map for setting the old id to a newly generated one
 * @param oldFormGroupIdToNewFormGroupId The map populated in {@link copyFormGroups} that has the old formGroup ids to their new ids
 * @param projectId The new projectId
 * @returns The images with new ids and updated references
 */
function copyImages(
	jsonImages: Image[],
	oldImageIdToNewImageId: Map<string, string>,
	oldFormGroupIdToNewFormGroupId: Map<string, string>,
	projectId: string,
): Image[] {
	const images = jsonImages.map((image) => {
		const newFormInstanceId = uuid();
		oldImageIdToNewImageId.set(image.id, newFormInstanceId);
		image.id = newFormInstanceId;
		const formGroupId = oldFormGroupIdToNewFormGroupId.get(image.formGroup);
		if (formGroupId) image.formGroup = formGroupId;
		image.project = projectId;
		return image;
	});
	return images;
}

/**
 * Given the old fieldInstances, loops through them, giving them new ids and updating their references to images, altInstances, tableFields, formGroups, and formInstances
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonFieldInstances The old fieldInstances
 * @param oldFieldInstanceIdToNewFieldInstanceId A map for setting the old id to a newly generated one
 * @param oldFormInstanceIdToNewFormInstanceId The map populated in {@link copyFormInstances} that has the old formInstance ids to their new ids
 * @param oldFormGroupIdToNewFormGroupId The map populated in {@link copyFormGroups} that has the old formGroup ids to their new ids
 * @param oldImageIdToNewImageId THe map populated in {@link copyImages} that has the old image ids to their new ids
 * @returns The new field with their new ids and updated references
 */
function copyFieldInstances(
	jsonFieldInstances: FieldInstanceForResolver[],
	oldFieldInstanceIdToNewFieldInstanceId: Map<string, string>,
	oldFormInstanceIdToNewFormInstanceId: Map<string, string>,
	oldFormGroupIdToNewFormGroupId: Map<string, string>,
	oldImageIdToNewImageId: Map<string, string>,
): FieldInstanceForResolver[] {
	// The final field instances
	const fieldInstances = [];
	// These next 2 arrays, I need to have all fields ran through before I can set their references because they reference other fieldInstances
	// Any field with .altInstanceId
	const altInstances = [];
	// Any field with .tableField
	const cellFields = [];
	// Fields that have a not-empty fieldsOrder array
	const tableFields = [];
	for (const fieldInstance of jsonFieldInstances) {
		const newFieldInstanceId = uuid();
		oldFieldInstanceIdToNewFieldInstanceId.set(fieldInstance.id, newFieldInstanceId);
		fieldInstance.id = newFieldInstanceId;
		const formGroupId = oldFormGroupIdToNewFormGroupId.get(fieldInstance.formGroup);
		if (formGroupId) fieldInstance.formGroup = formGroupId;
		if (fieldInstance.formInstance) {
			const newFormInstanceId = oldFormInstanceIdToNewFormInstanceId.get(fieldInstance.formInstance);
			if (newFormInstanceId) fieldInstance.formInstance = newFormInstanceId;
		}
		if (fieldInstance.imageId) {
			const newImageId = oldImageIdToNewImageId.get(fieldInstance.imageId);
			if (newImageId) fieldInstance.imageId = newImageId;
		}
		if (!fieldInstance.altInstanceId && !fieldInstance.tableField && (!fieldInstance.fieldsOrder || fieldInstance.fieldsOrder.length === 0)) {
			// regular, run of the mill field. Add to fieldInstances
			fieldInstances.push(fieldInstance);
		} else if (fieldInstance.altInstanceId) {
			altInstances.push(fieldInstance);
		} else if (fieldInstance.tableField) {
			cellFields.push(fieldInstance);
		} else if (fieldInstance.fieldsOrder && fieldInstance.fieldsOrder.length > 0) {
			tableFields.push(fieldInstance);
		}
	}

	// Handle the altInstances now
	for (const altInstance of altInstances) {
		if (altInstance.altInstanceId) {
			// altInstance.altInstanceId will be here, this is just to make ts happy
			const newAltInstanceId = oldFieldInstanceIdToNewFieldInstanceId.get(altInstance.altInstanceId);
			if (newAltInstanceId) altInstance.altInstanceId = newAltInstanceId;
			fieldInstances.push(altInstance);
		}
	}

	// Handle cells now
	for (const cell of cellFields) {
		if (cell.tableField) {
			// cell.tableField is here, just making ts happy
			const newTableId = oldFieldInstanceIdToNewFieldInstanceId.get(cell.tableField);
			if (newTableId) cell.tableField = newTableId;
			fieldInstances.push(cell);
		}
	}

	// Handle table cells
	for (const tableField of tableFields) {
		if (tableField.fieldsOrder) {
			// Convert csv array into new csv array
			tableField.fieldsOrder = tableField.fieldsOrder.map((rowCsv) =>
				rowCsv
					.split(",")
					.map((cellId) => oldFieldInstanceIdToNewFieldInstanceId.get(cellId))
					.join(","),
			);
			fieldInstances.push(tableField);
		}
	}
	return fieldInstances;
}

/**
 * Given the old revisions, loops through them, giving them new ids and updating their references to fieldInstances and formGroups
 * @author Eric Webb <ewebb@factorearth.com>
 * @param jsonRevisions The revisions from the json
 * @param oldFieldInstanceIdToNewFieldInstanceId The map populated in {@link copyFieldInstances} that has the old fieldInstance ids to the new ones
 * @param oldFormGroupIdToNewFormGroupId The map populated in {@link copyFormGroups} that has the old formGroup ids to the new ones
 * @returns The revisions with new ids and updated references
 */
function copyRevisions(
	jsonRevisions: Revision[],
	oldFieldInstanceIdToNewFieldInstanceId: Map<string, string>,
	oldFormGroupIdToNewFormGroupId: Map<string, string>,
): Revision[] {
	const revisions = jsonRevisions.map((revision) => {
		const newRevisionId = uuid();
		revision.id = newRevisionId;
		const newFieldInstanceId = oldFieldInstanceIdToNewFieldInstanceId.get(revision.fieldInstance);
		if (newFieldInstanceId) revision.fieldInstance = newFieldInstanceId;
		const newFormGroupId = oldFormGroupIdToNewFormGroupId.get(revision.formGroup);
		if (newFormGroupId) revision.formGroup = newFormGroupId;
		return revision;
	});
	return revisions;
}
